import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { api } from '../lib/api'
import OwnersModal from './OwnersModal'
import EditModal from './EditModal'
import ParticipantsModal from './ParticipantsModal'
import CreateUnitModal from './CreateUnitModal'
import AddStudentModal from './AddStudentModal'
import StudentManagementModal from './StudentManagementModal'
import UnitCard from './UnitCard'
import MembershipHistory from './MembershipHistory'
import TransitionWizard from './TransitionWizard'
import MetadataEditor from './MetadataEditor'
import PedagogicalNotes from './PedagogicalNotes'
import { 
  Plus, 
  Trash2, 
  Search,
  X,
  RefreshCw,
  History,
  ArrowRight,
  Database,
  FileText
} from 'lucide-react'
import toast from 'react-hot-toast'

interface UnitManagementPageProps {
  unitType: 'course' | 'group' | 'class'
  title: string
  description: string
  createButtonLabel: string
  searchPlaceholder?: string
  /** Stichtag für Anzeige (YYYY-MM-DD); null = heute */
  standAsOf?: string | null
}

export default function UnitManagementPage({
  unitType,
  title,
  description,
  createButtonLabel,
  searchPlaceholder = 'Suchen...',
  standAsOf = null
}: UnitManagementPageProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showParticipantsModal, setShowParticipantsModal] = useState(false)
  const [showTrash, setShowTrash] = useState(false)
  const [editingItem, setEditingItem] = useState<any>(null)
  const [participantsItem, setParticipantsItem] = useState<any>(null)
  const [showOwnersModal, setShowOwnersModal] = useState(false)
  const [ownersItem, setOwnersItem] = useState<any>(null)
  const [showAddStudentModal, setShowAddStudentModal] = useState(false)
  const [addStudentItem, setAddStudentItem] = useState<any>(null)
  const [showStudentManagementModal, setShowStudentManagementModal] = useState(false)
  const [studentManagementItem, setStudentManagementItem] = useState<any>(null)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [historyItem, setHistoryItem] = useState<any>(null)
  const [showTransitionWizard, setShowTransitionWizard] = useState(false)
  const [transitionItem, setTransitionItem] = useState<any>(null)
  const [showMetadataModal, setShowMetadataModal] = useState(false)
  const [metadataItem, setMetadataItem] = useState<any>(null)
  const [showPedagogicalNotesModal, setShowPedagogicalNotesModal] = useState(false)
  const [pedagogicalNotesItem, setPedagogicalNotesItem] = useState<any>(null)

  const queryClient = useQueryClient()

  // Fetch units (mit optionalem Stichtag für Mitgliederanzahl)
  const { data: unitsData, isLoading: unitsLoading, refetch: refetchUnits } = useQuery(
    [unitType, showTrash ? 'trash' : 'active', standAsOf ?? 'today'],
    () => api.get(`/units/${unitType}`, {
      params: {
        is_active: showTrash ? 'false' : 'true',
        ...(standAsOf ? { date: standAsOf } : {})
      }
    }).then(res => {
      console.log('Units API response:', res.data)
      return res.data
    }),
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true
    }
  )

  // Fetch subjects (für Kurse)
  const { data: subjectsData } = useQuery(
    'subjects',
    () => api.get('/assignments/subjects/public').then(res => res.data),
    {
      enabled: unitType === 'course'
    }
  )

  // Fetch curricula (für Kurse)
  const { data: curriculaData } = useQuery(
    'curricula',
    () => api.get('/assignments/curricula/public').then(res => res.data),
    {
      enabled: unitType === 'course'
    }
  )

  // Fetch jahrgang (für Kurse)
  const { data: jahrgangData } = useQuery(
    'jahrgang',
    () => api.get('/assignments/jahrgang/public').then(res => res.data),
    {
      enabled: unitType === 'course'
    }
  )

  const units = unitsData?.data || []
  const subjects = subjectsData?.data || []
  const curricula = curriculaData?.data || []
  const jahrgang = jahrgangData?.data || []

  // Filter units based on search term
  const filteredUnits = units.filter((unit: any) => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    
    if (unitType === 'class') {
      return (
        unit.class_name?.toLowerCase().includes(search) ||
        unit.grade_level?.toLowerCase().includes(search) ||
        unit.department?.toLowerCase().includes(search)
      )
    } else if (unitType === 'course') {
      return (
        unit.name?.toLowerCase().includes(search) ||
        unit.fach?.toLowerCase().includes(search) ||
        unit.jahrgang?.toLowerCase().includes(search) ||
        unit.ersteller_name?.toLowerCase().includes(search)
      )
    } else if (unitType === 'group') {
      return (
        unit.name?.toLowerCase().includes(search) ||
        unit.gruppen_typ?.toLowerCase().includes(search) ||
        unit.beschreibung?.toLowerCase().includes(search)
      )
    }
    return true
  })

  // Create unit mutation
  const createUnitMutation = useMutation(
    (data: any) => api.post(`/units/${unitType}`, data),
    {
      onSuccess: async (response) => {
        console.log('Create response:', response)
        // Invalidate alle Queries für diesen unitType
        await queryClient.invalidateQueries([unitType])
        // Explizit refetch
        await refetchUnits()
        setShowCreateModal(false)
        toast.success(`${title.slice(0, -1)} erfolgreich erstellt`)
      },
      onError: (error: any) => {
        console.error(`Error creating ${unitType}:`, error)
        toast.error(`Fehler beim Erstellen des ${title.slice(0, -1)}: ${error.response?.data?.error || error.message}`)
      }
    }
  )

  // Update unit mutation
  const updateUnitMutation = useMutation(
    ({ id, data }: { id: number; data: any }) => api.put(`/units/${unitType}/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([unitType])
        setShowEditModal(false)
        setEditingItem(null)
        toast.success(`${title.slice(0, -1)} erfolgreich aktualisiert`)
      },
      onError: (error: any) => {
        console.error(`Error updating ${unitType}:`, error)
        toast.error(`Fehler beim Aktualisieren des ${title.slice(0, -1)}`)
      }
    }
  )

  // Delete unit mutation
  const deleteUnitMutation = useMutation(
    (id: number) => api.delete(`/units/${unitType}/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([unitType])
        queryClient.invalidateQueries([unitType, 'trash'])
        toast.success(`${title.slice(0, -1)} erfolgreich gelöscht`)
      },
      onError: (error: any) => {
        console.error(`Error deleting ${unitType}:`, error)
        toast.error(`Fehler beim Löschen des ${title.slice(0, -1)}`)
      }
    }
  )

  // Restore unit mutation
  const restoreUnitMutation = useMutation(
    (id: number) => api.put(`/units/${unitType}/${id}/restore`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([unitType])
        queryClient.invalidateQueries([unitType, 'trash'])
        toast.success(`${title.slice(0, -1)} erfolgreich wiederhergestellt`)
      },
      onError: (error: any) => {
        console.error(`Error restoring ${unitType}:`, error)
        toast.error(`Fehler beim Wiederherstellen des ${title.slice(0, -1)}`)
      }
    }
  )

  // Add student mutation
  const addStudentMutation = useMutation(
    ({ unitId, studentIds }: { unitId: number; studentIds: number[] }) => 
      api.post(`/memberships`, {
        student_id: studentIds[0], // TODO: Massenaktionen unterstützen
        unit_type: unitType,
        unit_id: unitId,
        valid_from: new Date().toISOString().split('T')[0],
        status: 'active'
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([unitType])
        toast.success(`Schüler erfolgreich hinzugefügt`)
      },
      onError: (error: any) => {
        console.error('Error adding students:', error)
        toast.error('Fehler beim Hinzufügen der Schüler')
      }
    }
  )

  // Remove student mutation
  const removeStudentMutation = useMutation(
    ({ unitId, studentId }: { unitId: number; studentId: number }) => 
      api.post(`/memberships/${studentId}/end`, {
        reason: 'Entfernt aus Einheit'
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([unitType])
        toast.success('Schüler erfolgreich entfernt')
      },
      onError: (error: any) => {
        console.error('Error removing student:', error)
        toast.error('Fehler beim Entfernen des Schülers')
      }
    }
  )

  const handleCreateUnit = (data: any) => {
    createUnitMutation.mutate(data)
  }

  const handleEditUnit = (data: any) => {
    if (editingItem) {
      updateUnitMutation.mutate({ id: editingItem.id, data })
    }
  }

  const handleDeleteUnit = (id: number) => {
    if (confirm(`Sind Sie sicher, dass Sie diesen ${title.slice(0, -1)} löschen möchten?`)) {
      deleteUnitMutation.mutate(id)
    }
  }

  const handleRestoreUnit = (id: number) => {
    if (confirm(`Möchten Sie diesen ${title.slice(0, -1)} wiederherstellen?`)) {
      restoreUnitMutation.mutate(id)
    }
  }

  const handleEdit = (unit: any) => {
    setEditingItem(unit)
    setShowEditModal(true)
  }

  const handleParticipants = (unit: any) => {
    setParticipantsItem(unit)
    setShowParticipantsModal(true)
  }

  const handleOwners = (unit: any) => {
    setOwnersItem(unit)
    setShowOwnersModal(true)
  }

  const handleOpenAddStudents = (unit: any) => {
    setAddStudentItem(unit)
    setShowAddStudentModal(true)
  }

  const handleAddStudents = (studentIds: number[]) => {
    if (addStudentItem) {
      // TODO: Massenaktionen implementieren
      studentIds.forEach(studentId => {
        addStudentMutation.mutate({ unitId: addStudentItem.id, studentIds: [studentId] })
      })
      setShowAddStudentModal(false)
      setAddStudentItem(null)
    }
  }

  const handleStudentManagement = (unit: any) => {
    setStudentManagementItem(unit)
    setShowStudentManagementModal(true)
  }

  const handleHistory = (unit: any) => {
    setHistoryItem(unit)
    setShowHistoryModal(true)
  }

  const handleTransitions = (unit: any) => {
    setTransitionItem(unit)
    setShowTransitionWizard(true)
  }

  const handleMetadata = (unit: any) => {
    setMetadataItem(unit)
    setShowMetadataModal(true)
  }

  const handlePedagogicalNotes = (unit: any) => {
    setPedagogicalNotesItem(unit)
    setShowPedagogicalNotesModal(true)
  }

  const handleStudentsUpdated = () => {
    queryClient.invalidateQueries([unitType])
    queryClient.invalidateQueries('participants')
  }

  const handleRemoveStudent = (studentId: string) => {
    if (participantsItem) {
      removeStudentMutation.mutate({ unitId: participantsItem.id, studentId: parseInt(studentId) })
    }
  }

  if (unitsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600 dark:border-primary-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-gray-50 sm:truncate sm:text-3xl sm:tracking-tight">
            {title}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {description}
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:ml-4 md:mt-0">
          <button
            onClick={() => setShowTrash(!showTrash)}
            className={`btn btn-sm ${showTrash ? 'btn-danger' : 'btn-secondary'}`}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {showTrash ? 'Zurück' : 'Papierkorb'}
          </button>
          <button onClick={() => setShowCreateModal(true)} className="btn btn-primary btn-sm">
            <Plus className="mr-2 h-4 w-4" />
            {createButtonLabel}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filteredUnits.map((unit: any) => (
          <UnitCard
            key={unit.id}
            unit={unit}
            unitType={unitType}
            onEdit={handleEdit}
            onDelete={handleDeleteUnit}
            onParticipants={handleParticipants}
            onOwners={unitType !== 'class' ? handleOwners : undefined}
            onStudentManagement={handleStudentManagement}
            onHistory={handleHistory}
            onTransitions={handleTransitions}
            onMetadata={handleMetadata}
            onPedagogicalNotes={handlePedagogicalNotes}
            showTrash={showTrash}
            onRestore={handleRestoreUnit}
          />
        ))}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 dark:bg-black/70">
          <div className="card max-h-[90vh] w-full max-w-4xl overflow-y-auto p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-50">{createButtonLabel}</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <CreateUnitModal
              unitType={unitType}
              onClose={() => setShowCreateModal(false)}
              onSave={handleCreateUnit}
              subjects={subjects}
              curricula={curricula}
              jahrgang={jahrgang}
            />
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <EditModal
          item={editingItem}
          type={unitType}
          onClose={() => {
            setShowEditModal(false)
            setEditingItem(null)
          }}
          onSubmit={handleEditUnit}
          isLoading={updateUnitMutation.isLoading}
        />
      )}

      {/* Participants Modal */}
      {showParticipantsModal && participantsItem && (
        <ParticipantsModal
          item={participantsItem}
          type={unitType}
          onClose={() => {
            setShowParticipantsModal(false)
            setParticipantsItem(null)
          }}
          onRemoveStudent={handleRemoveStudent}
          isLoading={false}
          standAsOf={standAsOf}
        />
      )}

      {/* Owners Modal */}
      {showOwnersModal && ownersItem && unitType !== 'class' && (
        <OwnersModal
          item={ownersItem}
          type={unitType}
          onClose={() => {
            setShowOwnersModal(false)
            setOwnersItem(null)
          }}
        />
      )}

      {/* Add Student Modal */}
      {showAddStudentModal && addStudentItem && (
        <AddStudentModal
          course={addStudentItem}
          onClose={() => {
            setShowAddStudentModal(false)
            setAddStudentItem(null)
          }}
          onAddStudents={handleAddStudents}
        />
      )}

      {/* Student Management Modal */}
      {showStudentManagementModal && studentManagementItem && (
        <StudentManagementModal
          unitType={unitType}
          unitId={studentManagementItem.id}
          unitName={studentManagementItem.name || studentManagementItem.class_name}
          onClose={() => {
            setShowStudentManagementModal(false)
            setStudentManagementItem(null)
          }}
          onStudentsUpdated={handleStudentsUpdated}
        />
      )}

      {/* History Modal */}
      {showHistoryModal && historyItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 dark:bg-black/70">
          <div className="card max-h-[90vh] w-full max-w-4xl overflow-y-auto p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                Historie für {historyItem.name || historyItem.class_name}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Zeigt alle Änderungen an Zugehörigkeiten dieser Einheit
              </p>
            </div>
            <MembershipHistory
              unitType={unitType}
              unitId={historyItem.id}
              onClose={() => {
                setShowHistoryModal(false)
                setHistoryItem(null)
              }}
            />
          </div>
        </div>
      )}

      {/* Transition Wizard: Nachfolger festlegen & Übergänge durchführen */}
      {showTransitionWizard && transitionItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 dark:bg-black/70">
          <div className="card max-h-[90vh] w-full max-w-4xl overflow-y-auto p-6">
            <TransitionWizard
              unitType={unitType}
              unitId={transitionItem.id}
              unitName={unitType === 'class' ? transitionItem.class_name : transitionItem.name}
              onClose={() => {
                setShowTransitionWizard(false)
                setTransitionItem(null)
              }}
              onComplete={() => {
                setShowTransitionWizard(false)
                setTransitionItem(null)
              }}
            />
          </div>
        </div>
      )}

      {/* Metadata Editor */}
      {showMetadataModal && metadataItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 dark:bg-black/70">
          <div className="card max-h-[90vh] w-full max-w-4xl overflow-y-auto p-6">
            <MetadataEditor
              entityType="unit"
              entityId={metadataItem.id}
              unitType={unitType}
              onClose={() => {
                setShowMetadataModal(false)
                setMetadataItem(null)
              }}
            />
          </div>
        </div>
      )}

      {/* Pedagogical Notes */}
      {showPedagogicalNotesModal && pedagogicalNotesItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 dark:bg-black/70">
          <div className="card max-h-[90vh] w-full max-w-4xl overflow-y-auto p-6">
            <PedagogicalNotes
              entityType="unit"
              entityId={pedagogicalNotesItem.id}
              onClose={() => {
                setShowPedagogicalNotesModal(false)
                setPedagogicalNotesItem(null)
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

